import type { AttemptEvent, ChildProfile, ContentNode, SkillState } from "../types";

export function masteryLabel(skill: SkillState) {
  const pct = Math.round(skill.accuracy * 100);
  if (skill.mastery === "review due") return `${pct}% - review due`;
  return `${pct}% - ${skill.mastery}`;
}

export function nextActionForSkill(skill: SkillState) {
  if (skill.accuracy >= 0.8 && skill.stability < 0.7) {
    return "Move to spaced review before calling this fluent.";
  }
  if (skill.accuracy >= 0.8 && skill.supportLevel !== "none") {
    return "Fade supports gradually and check accuracy holds.";
  }
  if (skill.accuracy < 0.7) {
    return "Show a teach card before more independent practice.";
  }
  return "Keep mixed practice in the review queue.";
}

export function recommendContent(child: ChildProfile, nodes: ContentNode[]) {
  const fragileSkills = child.skills
    .filter((skill) => skill.mastery === "learning" || skill.mastery === "review due" || skill.stability < 0.65)
    .map((skill) => skill.id);

  return (
    nodes.find((node) => fragileSkills.includes(node.skill) && node.ageBand === child.ageBand) ??
    nodes.find((node) => fragileSkills.includes(node.skill)) ??
    nodes[0]
  );
}

export function applyAttempt(skill: SkillState, correct: boolean): SkillState {
  const accuracyShift = correct ? 0.06 : -0.08;
  const stabilityShift = correct ? 0.05 : -0.06;
  const nextAccuracy = clamp(skill.accuracy + accuracyShift, 0.05, 0.99);
  const nextStability = clamp(skill.stability + stabilityShift, 0.05, 0.99);
  let mastery = skill.mastery;
  let supportLevel = skill.supportLevel;

  if (nextAccuracy >= 0.9 && nextStability >= 0.82 && supportLevel === "none") {
    mastery = "fluent";
  } else if (nextAccuracy >= 0.8 && nextStability >= 0.68) {
    mastery = "secure";
    supportLevel = fadeSupport(supportLevel);
  } else if (nextAccuracy < 0.62 || !correct) {
    mastery = "learning";
  }

  return {
    ...skill,
    accuracy: nextAccuracy,
    stability: nextStability,
    mastery,
    supportLevel,
    lastPractised: "Just now"
  };
}

export function createAttempt(childId: string, contentNodeId: string, correct: boolean, hints: number): AttemptEvent {
  return {
    id: `attempt-${Date.now()}`,
    childId,
    contentNodeId,
    accuracy: correct ? 1 : 0,
    timeSeconds: correct ? 42 : 71,
    hints,
    supportLevel: hints > 0 ? "medium" : "low",
    repetitionCount: 1,
    modality: "touch",
    errorType: correct ? "none" : "concept",
    createdAt: new Date().toISOString()
  };
}

export function minutesLabel(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder}m`;
  return `${hours}h ${remainder}m`;
}

function fadeSupport(supportLevel: SkillState["supportLevel"]): SkillState["supportLevel"] {
  if (supportLevel === "high") return "medium";
  if (supportLevel === "medium") return "low";
  if (supportLevel === "low") return "none";
  return "none";
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
